import { useEffect, useState, useRef } from 'react';

import { YoutubePlayer, VimeoPlayer } from '$utilities/classes';

/* ========================================================================= */
/* History */
/* ========================================================================= */

/**
 * @hook useLocation
 * @return {object}
 *
 * Once rendered in the browser, provides the location object to the component.
 */
export const useLocation = () => {
    const [ location, setLocation ] = useState(null);

    useEffect(() => {
        const normalizedUrl = new URL(window.location.href);
        setLocation(normalizedUrl);
    }, []);

    return location;
};

/* ========================================================================= */
/* Storage */
/* ========================================================================= */

/**
 * @hook useLocalStorage
 * @param {string} namespace
 * @param {any} defaultValue
 * @param {object} config
 *     @property {boolean} shoultParse (optional)
 * @return {array}
 *
 * Once rendered in the browser, provides an interface for the local storage.
 */
export const useLocalStorage = (namespace, defaultValue, { shoultParse = true } = {}) => {
    const [ localStorageDrawer, setLocalStorageDrawer ] = useState(defaultValue);

    useEffect(() => {
        const data = localStorage.getItem(namespace);
        const preparedData = data ? (shoultParse ? JSON.parse(data) : data) : defaultValue;
        setLocalStorageDrawer(preparedData);
    }, [ namespace ]);

    const updateLocalStorageDrawer = data => {
        const preparedData = shoultParse ? JSON.stringify(data) : data;
        localStorage.setItem(namespace, preparedData);
        setLocalStorageDrawer(data);
    };

    return [ localStorageDrawer, updateLocalStorageDrawer ];
};

/* ========================================================================= */
/* Players */
/* ========================================================================= */

/**
 * @hook usePlayer
 *
 * Unified hook managing all the players (YouTube, Vimeo,…) in the same ecosystem.
 * 1. Once the element reference is provided, sets the player element (iframe).
 * 2. Once the player element is available, initializes the player.
 * 2.1. Determines which platform player should be used.
 * 2.2. Based on the determined platform, uses the equivalent interface (if available).
 * 2.3. If the video has been stopped from the player, propagates to the state.
 * 3. Propagates the play to the interface and mutates the state.
 * 4. Propagates the pause to the interface and mutates the state.
 */
export const usePlayer = () => {
    const [ playerElement, setPlayerElement ] = useState(null);
    const [ player, setPlayer ] = useState(null);
    const [ isPlaying, setIsPlaying ] = useState(false);
    const elementRef = useRef(null);

    /* [1] */
    useEffect(() => {
        if (!elementRef.current) {
            return;
        }

        const _playerElement = elementRef.current.matches('iframe')
            ? elementRef.current
            : elementRef.current.querySelector('iframe');
        setPlayerElement(_playerElement);
    }, [ elementRef.current ]);

    /* [2] */
    useEffect(() => {
        if (!playerElement) {
            return;
        }

        /* [2.1] */
        const platform = playerElement.src.match(/(youtube|vimeo)/) ? playerElement.src.match(/(youtube|vimeo)/)[0] : null;

        /* [2.2] */
        if (platform === 'youtube') {
            const _player = new YoutubePlayer(playerElement);
            _player.onEnd = () => setIsPlaying(false); /* [2.3] */
            setPlayer(_player);
        } else if (platform === 'vimeo') {
            const _player = new VimeoPlayer(playerElement);
            _player.onEnd = () => setIsPlaying(false); /* [2.3] */
            setPlayer(_player);
        }
    }, [ playerElement ]);

    /* [3] */
    const play = () => {
        player?.play();
        setIsPlaying(true);
    };

    const playAt = time => {
        player?.playAt(time);
        setIsPlaying(true);
    };

    /* [4] */
    const pause = () => {
        player?.pause();
        setIsPlaying(false);
    };

    return {
        elementRef,
        player,
        play,
        playAt,
        pause,
        isPlaying,
    };
};
